'use client'

import '../styles/address-search-input.css'

import { useTheme } from 'next-themes'
import { forwardRef, useEffect, useState } from 'react'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'
import { useFormContext } from 'react-hook-form'
import { CSSObjectWithLabel } from 'react-select'

interface GooglePlacesOption {
  label: string
  value: {
    place_id: string
    description?: string
  }
}

export interface AddressResult {
  streetNumber: string | null
  street: string | null
  city: string | null
  state: string | null
  stateCode: string | null
  county: string | null
  postalCode: string | null
  postalCodeSuffix: string | null
  country: string | null
  countryCode: string | null
  addressLabel: string | null
  latitude: number
  longitude: number
  placeID: string | null
  type: string[] | null
}

export interface AddressSearchInputProps {
  initialValue?: GooglePlacesOption
  name: string
  placeholder?: string
  onSelect?: (event: CustomEvent<AddressResult>) => void
  onClear?: () => void
  currentTheme?: 'light' | 'dark'
  autoFocus?: boolean
}

interface CustomStyles {
  control: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  container: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  indicatorsContainer: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  input: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  menu: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  option: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => CSSObjectWithLabel
  placeholder: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  singleValue: (base: CSSObjectWithLabel) => CSSObjectWithLabel
  valueContainer: (base: CSSObjectWithLabel) => CSSObjectWithLabel
}

const AddressSearchInput = forwardRef<HTMLDivElement, AddressSearchInputProps>(
  ({ initialValue, name, placeholder, onSelect, onClear, currentTheme, autoFocus }, forwardedRef) => {
    const [inputValue, setInputValue] = useState<GooglePlacesOption | null>(null)
    const { theme } = useTheme() // Use theme context to detect dark mode
    const isDarkMode = currentTheme === 'dark' || theme === 'dark'

    const formContext = useFormContext()
    const setValue = formContext?.setValue
    const getValues = formContext?.getValues
    const address = getValues ? getValues(name) : null

    const handleChange = async (place: GooglePlacesOption | null) => {
      if (!place) {
        setInputValue(null)
        onClear?.()
        return
      }

      try {
        const results = await geocodeByPlaceId(place.value.place_id)
        const formattedAddress = getAddressComponents(results[0], place.label)
        if (setValue) {
          setValue(name, formattedAddress)
        }

        if (typeof onSelect === 'function') {
          const event = new CustomEvent('placeSelect', { detail: formattedAddress })
          onSelect(event)
        }
      } catch (error) {
        console.error('Error geocoding address:', error)
      }
    }

    useEffect(() => {
      if (address?.addressLabel || address?.city) {
        setInputValue({
          label: address.addressLabel?.toString() || '',
          value: {
            place_id: address.placeID || '',
            description: address.addressLabel?.toString()
          }
        })
      }
    }, [address])

    const customStyles: CustomStyles = {
      container: (provided) => ({
        ...provided,
        width: '100%'
      }),
      control: (provided) => ({
        ...provided,
        minHeight: '32px',
        height: '32px',
        padding: 0
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: '32px',
        padding: '0 4px',
        display: 'flex',
        alignItems: 'center'
      }),
      placeholder: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
        fontSize: '0.75rem',
        lineHeight: '1',
        margin: 0,
        padding: 0,
        gridArea: '1 / 1 / 2 / 2'
      }),
      singleValue: (provided) => ({
        ...provided,
        color: isDarkMode ? 'rgb(229, 231, 235)' : 'rgb(17, 24, 39)',
        fontSize: '0.75rem',
        lineHeight: '1',
        margin: 0,
        padding: 0,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        gridArea: '1 / 1 / 2 / 2'
      }),
      input: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
        color: isDarkMode ? 'rgb(229, 231, 235)' : 'rgb(17, 24, 39)',
        fontSize: '0.75rem',
        lineHeight: '1',
        margin: 0,
        padding: 0,
        width: '100%',
        outline: 'none',
        transition: 'border-color 0.15s ease-in-out',
        gridArea: '1 / 1 / 2 / 2',
        display: 'flex',
        alignItems: 'center',
        '& input': {
          width: '100% !important',
          textAlign: 'left'
        },
        '&::after': {
          display: 'none !important'
        }
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: '0 8px',
        height: '32px',
        minHeight: '32px',
        display: 'grid',
        gridTemplateColumns: '1fr',
        alignItems: 'center',
        flex: 1,
        minWidth: 0
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: isDarkMode
          ? state.isFocused
            ? 'rgb(55, 65, 81)'
            : 'rgb(31, 41, 55)'
          : state.isFocused
            ? 'rgb(243, 244, 246)'
            : 'white',
        color: isDarkMode ? 'rgb(229, 231, 235)' : 'rgb(17, 24, 39)',
        padding: '0.5rem 0.75rem',
        cursor: 'pointer'
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#09090b' : 'white',
        border: isDarkMode ? '1px solid rgb(75, 85, 99)' : '1px solid rgb(226, 232, 240)',
        borderRadius: '0.375rem',
        marginTop: '0.25rem',
        boxShadow: isDarkMode ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 1000
      })
    }

    return (
      <div className={isDarkMode ? 'dark-mode-address-search' : ''} ref={forwardedRef} style={{ position: 'relative' }}>
        <GooglePlacesAutocomplete
          apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}
          selectProps={{
            value: inputValue || initialValue,
            onChange: handleChange,
            placeholder,
            isClearable: true,
            classNamePrefix: 'google-places',
            noOptionsMessage: () => null,
            openMenuOnClick: false,
            styles: customStyles,
            menuPosition: 'absolute',
            menuPlacement: 'auto',
            autoFocus: autoFocus
          }}
        />
      </div>
    )
  }
)

function getAddressComponents(place: google.maps.GeocoderResult, addressLabel: string): AddressResult {
  let streetNumber = ''
  let street = ''
  let city = ''
  let state = ''
  let stateCode = ''
  let county = ''
  let postalCode = ''
  let postalCodeSuffix = ''
  let country = ''
  let countryCode = ''
  let latitude = 0
  let longitude = 0
  let placeID = ''
  let type: string[] = []

  latitude = place.geometry.location.lat()
  longitude = place.geometry.location.lng()
  placeID = place.place_id
  type = place.types

  for (const component of place.address_components) {
    const addressType = component.types[0]

    switch (addressType) {
      case 'street_number':
        streetNumber = component.long_name
        break
      case 'route':
        street = component.long_name
        break
      case 'locality':
        city = component.long_name
        break
      case 'administrative_area_level_2':
        county = component.long_name
        break
      case 'administrative_area_level_1':
        state = component.long_name
        stateCode = component.short_name
        break
      case 'postal_code':
        postalCode = component.long_name
        break
      case 'postal_code_suffix':
        postalCodeSuffix = component.long_name
        break
      case 'country':
        country = component.long_name
        countryCode = component.short_name
        break
    }
  }

  return {
    streetNumber: streetNumber || null,
    street: street || null,
    city: city || null,
    county: county || null,
    state: state || null,
    stateCode: stateCode || null,
    postalCode: postalCode || null,
    postalCodeSuffix: postalCodeSuffix || null,
    country: country || null,
    countryCode: countryCode || null,
    addressLabel,
    latitude,
    longitude,
    placeID,
    type
  }
}

export { AddressSearchInput }
